import { HttpClient } from '@angular/common/http';
import {
	ChangeDetectorRef,
	Directive,
	ElementRef,
	HostListener,
	ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
	AsyncSubject,
	combineLatest,
	NEVER,
	Observable,
	of,
	Subject,
} from 'rxjs';
import {
	catchError,
	delay,
	distinctUntilChanged,
	finalize,
	map,
	switchMap,
	takeUntil,
	tap,
} from 'rxjs/operators';
import spacetime, { type Spacetime } from 'spacetime';
import { getEnvironment } from 'src/lib/environment/environment';
import { ICanExitItem, IConfirmExit } from 'src/lib/guards/confirm-exit.guard';
import { StudentCurrentCourseModel } from 'src/lib/services/api/students/classes/current-courses/student-current-course.model';
import {
	StudentCourseActivitiesFolderModel,
	StudentCourseActivitiesModel,
	StudentCourseActivityFolderModel,
} from 'src/lib/services/api/students/classes/student-course.model';
import { StudentCourseService } from 'src/lib/services/api/students/classes/student-course.service';
import { StudentOverviewModel } from 'src/lib/services/api/students/student-overview.model';
import { ActiveStudentService } from 'src/lib/services/startup/active-student/active-student.service';
import { SessionStoreService } from 'src/lib/services/stores/session-store/session-store.service';
import { StudentCourseStoreService } from 'src/lib/services/stores/students/course/student-course-store.service';
import { StudentStoreService } from 'src/lib/services/stores/students/student/student-store.service';
import { AlertPopupService } from 'src/lib/services/utility/alert-popup/alert-popup.service';
import { ConfirmPopupService } from 'src/lib/services/utility/confirm-popup/confirm-popup.service';
import { InitializationService } from 'src/lib/services/utility/initialization/initialization.service';
import { TypedStoreType } from 'src/lib/types/typed-storage';
import { hasValue } from 'src/lib/utilities/compare';
import { tryParseInt } from 'src/lib/utilities/convert';
import { SessionLogger } from 'src/lib/utilities/logging/session-logger';
import {
	getRegistrationActivityAccessState,
	RegistrationActivityAccessState,
} from 'src/lib/utilities/site/course';

export enum CourseActivityPlayerSignalMessage {
	scormExitClicked = 'gradally.scorm.exit-clicked',
	scormCompleted = 'gradally.scorm.completed',
	scormExitCommitted = 'gradally.scorm.exit-committed',
	scormExitCommitFailed = 'gradally.scorm.exit-commit-failed',
	assignFileDraftUploaded = 'gradally.assign.file-draft-uploaded',
	assignFileUploaded = 'gradally.assign.file-uploaded',
	ltiCompleted = 'gradally.lti.completed',
	quizCompleted = 'gradally.quiz.completed',
}

export interface CourseActivityPlayerSignalMessagePayload {
	type: CourseActivityPlayerSignalMessage;
	moodleOrigin?: string;
}

const courseActivityPlayerSignalMessages: ReadonlySet<string> = new Set<string>(
	Object.values(CourseActivityPlayerSignalMessage),
);

@Directive()
export abstract class AbstractCourseActivityPlayerComponent implements IConfirmExit {
	protected _unsubscribe$ = new AsyncSubject<null>();

	protected loadingImageUrl = `${getEnvironment().assets}/images/ga_loader.gif`;
	public RegistrationActivityAccessState = RegistrationActivityAccessState;

	public course: StudentCurrentCourseModel;
	public studentClassActivities: StudentCourseActivitiesModel;
	public studentUid: number;
	public registrationId: number;
	public activityId: string;
	public overview: StudentOverviewModel;

	public activities: StudentCourseActivityFolderModel[] = [];
	public activityFolderMap: Map<string, StudentCourseActivitiesFolderModel>;
	public activityIndex: number;
	public activityReady: boolean;
	public activityLoadState: string = 'Initializing';
	public activityAccessState: RegistrationActivityAccessState;
	public activityUrl: string;
	public activityLinkType: string;
	public activityType: 'lesson' | 'assignment';
	public activityActualType: string;
	public hasVerifiedLMS: boolean = false;

	public showCover: boolean;
	public isMasquerading: boolean;

	public showSectionWelcome: boolean = false;
	public sectionWelcomeFadeOut: boolean = false;
	public sectionSliderSubject = new Subject<null>();
	public sectionTotalCount: number;
	private sectionWelcomeCancelSubject = new Subject<void>();

	protected assignmentStart: Spacetime;

	@ViewChild('activityFrame')
	public activityFrame: ElementRef<HTMLIFrameElement>;

	constructor(
		protected route: ActivatedRoute,
		protected activeStudent: ActiveStudentService,
		protected studentsClassesStore: StudentCourseStoreService,
		protected alertPopupService: AlertPopupService,
		protected cdr: ChangeDetectorRef,
		protected httpClient: HttpClient,
		protected sessionStore: SessionStoreService,
		protected studentStore: StudentStoreService,
		protected initService: InitializationService,
		protected confirmPopupService: ConfirmPopupService,
		protected studentCourseService: StudentCourseService,
	) {}

	@HostListener('window:message', ['$event'])
	public onWindowMessage(event: MessageEvent): void {
		const signalMessage = this.validateSignalMessage(event);
		if (signalMessage != null) {
			SessionLogger.log(
				'CourseActivityPlayer',
				'Message received from Moodle frame.',
				{ type: signalMessage },
			);
			this.handleSignalMessage(signalMessage);
		}
	}

	protected abstract handleSignalMessage(
		signalMessage: CourseActivityPlayerSignalMessage,
	): void;

	baseOnInit(): void {
		this.showCover = this.showCover ?? this.initService.impersonaterId != null;
		this.isMasquerading = this.initService.impersonaterId != null;

		this.sectionSliderSubject
			.pipe(
				tap(() => {
					this.showSectionWelcome = false;
					this.sectionWelcomeFadeOut = false;
					this.cdr.detectChanges();
				}),
				switchMap(() =>
					of(null).pipe(
						delay(2000),
						tap(() => {
							this.showSectionWelcome = true;
							this.cdr.detectChanges();
						}),
						delay(5000),
						tap(() => {
							this.sectionWelcomeFadeOut = true;
							this.cdr.detectChanges();
						}),
						takeUntil(this.sectionWelcomeCancelSubject),
					),
				),
				takeUntil(this._unsubscribe$),
			)
			.subscribe();

		// The token can expire before it should, we are anticipating that users will try refreshing
		// the page. So on a rapid refresh, try refetching the token
		let forceRefreshToken = false;
		const lastLoadTimestamp = this.sessionStore.get(
			'sap-course-activity-player-last-loaded',
			TypedStoreType.NUMBER,
		);

		if (
			lastLoadTimestamp != null &&
			new Date() < new Date(lastLoadTimestamp + 30 * 1000) // 30 Seconds
		) {
			forceRefreshToken = true;
		}

		this.route.params
			.pipe(
				map((p) => {
					return tryParseInt(p.registrationId);
				}),
				distinctUntilChanged(),
				switchMap((r) =>
					combineLatest([this.activeStudent.getActiveStudent$(), of(r)]),
				),
				switchMap(([studentId, registrationId]) => {
					this.activityLoadState = 'Loading Activity';
					this.cdr.detectChanges();

					return combineLatest([
						this.studentsClassesStore.classActivities$(
							studentId,
							registrationId,
						),
						this.studentsClassesStore.current$(studentId),
						of(studentId),
						of(registrationId),
						this.studentStore.overview$(studentId),
					]);
				}),
				switchMap(
					([
						studentClassActivities,
						currentClasses,
						studentId,
						registrationId,
						overview,
					]) =>
						this.route.params.pipe(
							map(
								(p) =>
									[
										studentClassActivities,
										currentClasses,
										studentId,
										registrationId,
										p.activityId,
										overview,
									] as [
										StudentCourseActivitiesModel,
										StudentCurrentCourseModel[],
										number,
										number,
										string,
										StudentOverviewModel,
									],
							),
						),
				),
				switchMap((x) => {
					this.assignmentStart = spacetime(new Date());
					const nextTimeBuffered = x[0].next_token_time - 5 * 60; // 5 minutes
					if (hasValue(x[0].token_url)) {
						// Check for SSO token refresh
						if (
							forceRefreshToken ||
							new Date() > new Date(nextTimeBuffered * 1000)
						) {
							this.sessionStore.remove(
								'sap-course-activity-player-last-loaded',
							);

							this.activityLoadState = 'Logging Into Player';
							this.cdr.detectChanges();

							window.location.href = `${
								x[0].token_url
							}?back=${encodeURIComponent(window.location.href)}`;
							return NEVER;
						}
					}

					return of(x);
				}),
				takeUntil(this._unsubscribe$),
			)
			.subscribe(
				([
					studentClassActivities,
					currentClasses,
					studentId,
					registrationId,
					activityId,
					overview,
				]) => {
					this.sessionStore.set(
						'sap-course-activity-player-last-loaded',
						new Date().getTime(),
					);

					this.studentClassActivities = studentClassActivities;
					this.studentUid = studentId;
					this.registrationId = registrationId;
					this.activityId = activityId;
					this.course = currentClasses.find(
						(c) => c.registration_id === registrationId,
					);
					this.overview = overview;

					const previousSection =
						this.activityIndex != null
							? this.getShortFolderName(
									this.activityFolderMap.get(
										this.activities[this.activityIndex].id,
									),
								)
							: null;

					this.sectionTotalCount = this.studentClassActivities.folders.length;

					this.activityFolderMap = new Map();

					this.studentClassActivities.folders.forEach((f) =>
						f.activities.forEach((a) => this.activityFolderMap.set(a.id, f)),
					);

					this.activities = this.studentClassActivities.folders
						.map((x) => x.activities.filter((a) => !a.hidden))
						.flat();
					this.activityIndex = this.activities.findIndex(
						(a) => a.id === activityId,
					);

					this.activityReady = true;
					this.activityAccessState = getRegistrationActivityAccessState(
						this.activities[this.activityIndex],
					);

					this.activityUrl = this.activities[this.activityIndex].embed_link;
					this.activityType = this.activities[this.activityIndex].type;
					this.activityLinkType = this.activities[this.activityIndex].link_type;
					this.activityActualType =
						this.activities[this.activityIndex].actual_type;

					if (
						this.activityLinkType === 'ga_lms_moodle_link' &&
						!this.hasVerifiedLMS
					) {
						this.hasVerifiedLMS = true;
						this.activityReady = false;
						this.verifyMoodleLogin();
					}

					const currentSection = this.getShortFolderName(
						this.activityFolderMap.get(this.activities[this.activityIndex].id),
					);

					const folderActivityIndex = this.studentClassActivities.folders
						.find((f) => this.getShortFolderName(f) === currentSection)
						.activities.findIndex(
							(a) => a.id === this.activities[this.activityIndex].id,
						);

					if (
						(previousSection == null && folderActivityIndex === 0) ||
						(previousSection != null && previousSection !== currentSection)
					) {
						this.sectionSliderSubject.next(null);
					}

					this.studentsClassesStore.clearClassActivities(
						this.studentUid,
						this.registrationId,
					);

					this.cdr.detectChanges();
				},
			);
	}

	protected cancelSectionWelcome(): void {
		this.sectionWelcomeCancelSubject.next();
		this.showSectionWelcome = false;
		this.sectionWelcomeFadeOut = false;
		this.cdr.detectChanges();
	}

	protected clearAssignmentExitCheck(): void {
		this.assignmentStart = null;
	}

	public verifyMoodleLogin = () => {
		const url = new URL(this.activityUrl);
		this.httpClient
			.get<unknown>(
				`${url.origin}/local/gradally/login/verify_login_user.php?userId=${this.studentClassActivities.external_id}`,
				{},
			)
			.pipe(
				finalize(() => {
					this.activityReady = true;
					this.cdr.detectChanges();
				}),
			)
			.subscribe();
	};

	private validateSignalMessage = (
		event: MessageEvent,
	): CourseActivityPlayerSignalMessage | null => {
		if (
			event == null ||
			event.data == null ||
			typeof event.data !== 'object' ||
			this.activityLinkType !== 'ga_lms_moodle_link'
		) {
			return null;
		}

		const payload =
			event.data as Partial<CourseActivityPlayerSignalMessagePayload>;
		if (!this.isSignalMessageType(payload.type)) {
			return null;
		}

		const frame = this.activityFrame?.nativeElement;
		if (frame?.contentWindow == null || event.source !== frame.contentWindow) {
			return null;
		}

		const expectedOrigin = this.getActivityOrigin();
		if (expectedOrigin == null || event.origin !== expectedOrigin) {
			return null;
		}

		if (
			payload.moodleOrigin != null &&
			payload.moodleOrigin !== expectedOrigin
		) {
			return null;
		}

		return payload.type;
	};

	private isSignalMessageType = (
		type: unknown,
	): type is CourseActivityPlayerSignalMessage => {
		return (
			typeof type === 'string' && courseActivityPlayerSignalMessages.has(type)
		);
	};

	private getActivityOrigin = (): string | null => {
		if (
			typeof this.activityUrl !== 'string' ||
			this.activityUrl.trim() === ''
		) {
			return null;
		}

		try {
			return new URL(this.activityUrl, window.location.href).origin;
		} catch {
			return null;
		}
	};

	public getActivityPositionString = (
		activity: StudentCourseActivityFolderModel,
	) => {
		if (activity.type === 'assignment') {
			const folder = this.activityFolderMap.get(activity.id);
			const folderAssignments = folder.activities.filter(
				(a) => a.type === 'assignment',
			);
			return ` - Assignment ${
				folderAssignments.findIndex((a) => a.id === activity.id) + 1
			} of ${folderAssignments.length}`;
		} else return '';
	};

	public getShortFolderName = (folder: StudentCourseActivitiesFolderModel) => {
		let sliceIndex = folder.metadata.title.indexOf(':');
		if (sliceIndex === -1) {
			sliceIndex = folder.metadata.title.length;
		}

		return folder.metadata.title.slice(0, sliceIndex);
	};

	public viewProctorPassword = () => {
		this.alertPopupService
			.alert$({
				title: 'Final Password',
				message: `The final exam password is: ${this.course.proctor_password}`,
			})
			.subscribe();
	};

	public disableWarning = () => {
		this.showCover = false;
	};

	canExit$(): Observable<ICanExitItem> {
		if (
			this.activities == null ||
			this.activities[this.activityIndex] == null ||
			this.assignmentStart == null
		) {
			return of({ safeExit: true });
		}

		if (
			this.activities[this.activityIndex].type === 'assignment' &&
			this.assignmentStart.diff(new Date(), 'minutes') >= 1
		) {
			return this.studentCourseService
				.getCourseProgressReport(this.studentUid, this.registrationId)
				.pipe(
					catchError(() => of(null)),
					switchMap((x) => {
						let obs$ = of(true);
						if (hasValue(x)) {
							const c = x.items.find(
								(item) => item.id === this.activities[this.activityIndex].id,
							);

							if (!hasValue(c.submitted_date)) {
								//not submitted
								obs$ = this.confirmPopupService.confirm$({
									message: `No submission was found for ${this.activities[this.activityIndex].name}. Did you forget to submit your work?`,
									noText: 'Go Back',
									yesText: 'Continue',
								});
							}
						}

						return obs$.pipe(
							switchMap((val) => {
								return of({ safeExit: val });
							}),
						);
					}),
				);
		} else {
			return of({ safeExit: true });
		}
	}

	baseOnDestroy(): void {
		this.sectionWelcomeCancelSubject.next();
		this.sectionWelcomeCancelSubject.complete();
		this._unsubscribe$.next(null);
		this._unsubscribe$.complete();
		this._unsubscribe$ = null;
	}
}
